import get from 'lodash/get';
import {
  fieldStarterSeat,
  fieldOfficeSeats,
  keyDevices
} from '../../utils/constant';

/**
 * Hardware Credit (HWC) allowance rules for BCON primary devices.
 * Pure module: no React, network or locale-file imports. Localised values arrive
 * through `contentfulValues` ({ valuePaymentUpfront }).
 */

// Mirrors formCommon.getFieldValue; not imported to keep this module free of request/DOM dependencies.
const readFieldValue = (fieldName, fields) =>
  fields &&
  Object.prototype.hasOwnProperty.call(fields, fieldName) &&
  fields[fieldName].isVisible
    ? fields[fieldName].value
    : '';

const toCount = value => {
  const number = Number(value);
  return Number.isFinite(number) && number > 0 ? number : 0;
};

/**
 * A Device Matrix entry is HWC when its multi-select `hardwareCredir` answer
 * includes Yes/Oui, even if No/Non is selected alongside it.
 * @param {object} apiDevice - Device from GET /orderDevices
 * @returns {boolean}
 */
export const supportsHardwareCredit = apiDevice => {
  const answer = get(apiDevice, 'answers.hardwareCredir');
  if (!answer) return false;

  return String(answer)
    .split(',')
    .map(token => token.trim().toLowerCase())
    .some(token => token === 'yes' || token === 'oui');
};

/** Starter + Office seats; Limited Extensions never grant HWC entitlement. */
export const calculateHwcAllowance = (formFields, orderData = {}) => {
  const starterSeats = toCount(
    readFieldValue(fieldStarterSeat, formFields) ||
      get(orderData, fieldStarterSeat, 0)
  );
  const officeSeats = toCount(
    readFieldValue(fieldOfficeSeats, formFields) ||
      get(orderData, fieldOfficeSeats, 0)
  );
  return starterSeats + officeSeats;
};

/** Quantity of Upfront primary lines flagged HWC, whether or not the HWC promo is selected yet. */
export const countHwcDevices = (formFields, contentfulValues) => {
  const upfrontValue = get(contentfulValues, 'valuePaymentUpfront');
  const lines = get(formFields, keyDevices);
  if (!upfrontValue || !Array.isArray(lines)) return 0;

  return lines.reduce(
    (total, line) =>
      line && line.hwcEligible === true && line.payForDevice === upfrontValue
        ? total + toCount(line.quantity)
        : total,
    0
  );
};

/**
 * @param {object} options
 * @param {number} [options.additionalQuantity] - Quantity of a pending HWC line being added
 * @param {number} [options.replacingQuantity] - Quantity of an existing HWC line being replaced
 * @param {number} [options.allowanceOverride] - Seat basis supplied by the caller (recontract)
 */
export const checkHwcAllowance = (
  formFields,
  orderData,
  contentfulValues,
  options = {}
) => {
  const {
    additionalQuantity = 0,
    replacingQuantity = 0,
    allowanceOverride
  } = options;

  const allowance = Number.isFinite(allowanceOverride)
    ? toCount(allowanceOverride)
    : calculateHwcAllowance(formFields, orderData);

  const current = countHwcDevices(formFields, contentfulValues);
  const additional = toCount(additionalQuantity);
  const projected =
    Math.max(0, current - toCount(replacingQuantity)) + additional;

  return {
    allowance,
    current,
    remaining: Math.max(0, allowance - current),
    isExceeded: current > allowance,
    // Only a pending HWC add can trip this; a non-HWC add must not be blocked by a pre-existing excess.
    wouldExceed: additional > 0 && projected > allowance
  };
};
